import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ulid } from 'ulid';

import { apiPatch } from '../lib/api_v2';
import { getChannelDisplayName } from '../lib/channels';
import db from '../lib/db';
import { logDebug, logError, logWarn } from '../lib/logger';
import { MediaAttachmentOwnerTypes, setMediaAttachmentsOwner } from '../lib/media_attachments_store';
import { optimisticMessageOwnerId } from '../lib/optimistic_messages';
import { containsOnlyWhitespace } from '../lib/stringUtils';
import { useRetainedFeedMessage } from '../hooks/useRetainedFeedMessage';

import { useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import RichdownComposer from './RichdownComposer';

const MessagesFooter = ({
  channelId,
  editingMessageId = null,
  editingMessageSnapshot = null,
  onCancelEditing,
  dropzoneHelpers = null,
  onDropzoneStateChange = null,
  onUploadCountChange = null,
  maxMediaAttachments = 0,
  maxRichdownContentSize = 0,
  currentChannel = null,
  isInteractive = true,
  onOwnMessageSubmitted,
}) => {
  const composerRef = useRef(null);
  const editingMessageIdRef = useRef(editingMessageId);
  const cancelEditingRef = useRef(onCancelEditing); // keep latest handler accessible inside Dexie hook callbacks
  // Retain the reply target row alongside its id: the banner shows the author from
  // this snapshot, while submission only ever sends the stable id.
  const [replyTo, setReplyTo] = useState(null);
  const replyToMessageId = replyTo?.messageId ?? null;
  const { profileId: currentProfileId, mainChannelId } = useRouteWorkspace();
  const workspaceId = currentChannel?.workspace_id;

  useEffect(() => {
    editingMessageIdRef.current = editingMessageId;
  }, [editingMessageId]);

  useEffect(() => {
    cancelEditingRef.current = onCancelEditing;
  }, [onCancelEditing]);

  // listen for reply-to-message events
  useEffect(() => {
    const handleReplyToMessage = (e) => {
      const { messageId, channelId: eventChannelId, message } = e.detail;
      // only set reply if it's for the current channel
      if (eventChannelId === channelId) {
        // if currently editing, cancel edit mode first
        if (editingMessageIdRef.current) {
          cancelEditingRef.current?.();
        }
        setReplyTo({ messageId, message: message || null });
        // focus the composer
        composerRef.current?.focus?.();
      }
    };

    window.addEventListener('output:reply-to-message', handleReplyToMessage);
    return () => {
      window.removeEventListener('output:reply-to-message', handleReplyToMessage);
    };
  }, [channelId]);

  // clear reply when channel changes
  useEffect(() => {
    setReplyTo(null);
  }, [channelId]);

  // clear reply when entering edit mode
  useEffect(() => {
    if (editingMessageId) {
      setReplyTo(null);
    }
  }, [editingMessageId]);

  // Listen to deletes so we can cancel the edit if the message being edited is deleted
  useEffect(() => {
    function onMessageDeleting(primKey) {
      if (primKey !== editingMessageIdRef.current) return;

      this.onsuccess = () => {
        if (editingMessageIdRef.current !== primKey) return;
        logDebug('MessagesFooter', 'Clearing editing state after delete', primKey);
        cancelEditingRef.current?.();
      };
    }

    db.messages.hook('deleting', onMessageDeleting);

    return () => {
      db.messages.hook('deleting').unsubscribe(onMessageDeleting);
    };
  }, []);

  const editorId = useMemo(() => (channelId ? `channel-${channelId}` : 'channel-default'), [channelId]);

  // The durable feed row wins while it exists (stays live while editing); the
  // renderer-row snapshot retained at edit entry is the fallback. Every edit
  // dispatcher supplies the snapshot, so there is no further store to consult.
  const { message: messageBeingEdited, deleted: editTargetDeleted } = useRetainedFeedMessage(
    editingMessageId,
    editingMessageSnapshot,
  );

  // Sync-originated deletions never dispatch the live-journal destroy event; the feed
  // row disappearing is the deletion signal for those (e.g. the message was deleted on
  // another device while this client was offline).
  useEffect(() => {
    if (!editTargetDeleted) return;
    logDebug('MessagesFooter', 'Clearing editing state after feed delete', editingMessageIdRef.current);
    cancelEditingRef.current?.();
  }, [editTargetDeleted]);

  const handleSubmitMessage = useCallback(
    async (message, attachments = []) => {
      const normalizedMessage = message || '';
      const trimmedMessage = normalizedMessage.trim();
      const normalizedAttachments = Array.isArray(attachments) ? attachments : [];
      // `attachment_id` = server-side attachment; `id` = local Dexie upload row
      // (still uploading/processing — lets the optimistic message track progress).
      const attachmentIds = normalizedAttachments.map((attachment) => attachment.attachment_id).filter(Boolean);
      const uploadIds = normalizedAttachments.map((attachment) => attachment.id).filter(Boolean);
      const isEmptyWithoutAttachments = containsOnlyWhitespace(trimmedMessage) && normalizedAttachments.length === 0;

      if (!channelId) {
        logWarn('RichdownComposer', 'Cannot submit message without channelId');
        return false;
      }
      if (!workspaceId) {
        logWarn('RichdownComposer', 'Cannot submit message without workspaceId');
        return false;
      }

      // An edit target that resolved nowhere must never be patched blind.
      //
      // Defensive only — treat it as untested. Both entry points into edit mode supply a
      // renderer row (ContextMenuPortal always sends one; MessageFeed only opens the
      // Arrow-Up target once it has resolved a row), and useRetainedFeedMessage falls back
      // to that snapshot, so this is not reachable today. The one case that does lose the
      // feed row mid-edit — a delete arriving through sync — keeps the snapshot and is
      // handled by the editTargetDeleted effect above instead, which leaves a single-tick
      // race where Enter can still PATCH a just-deleted message and take a 404.
      if (editingMessageId && !messageBeingEdited) {
        logWarn('MessagesFooter', 'Cannot submit edit without resolved message data', { editingMessageId });
        return false;
      }

      if (!editingMessageId && isEmptyWithoutAttachments) {
        logDebug('RichdownComposer', 'Skipping submission - no content');
        return false;
      }

      // Feels like we should not just have this in this footer, but somewhere more centralized.
      // Could be livejournal but there's nothing inherently
      if (editingMessageId) {
        if (isEmptyWithoutAttachments) {
          window.dispatchEvent(
            new CustomEvent('output:internal:messages:delete-message-modal', {
              detail: { messageId: editingMessageId, message: messageBeingEdited || null },
            }),
          );
          return;
        }

        try {
          const response = await apiPatch(`/channels/${channelId}/messages/${editingMessageId}`, {
            message: {
              text: trimmedMessage,
              attachments: attachmentIds,
            },
          });

          const data = await response.json();

          // TODO: need to handle the error here, put the message back in the editor and show the error
          if (!response.ok || !data.ok) {
            onCancelEditing?.();
            alert('Failed to update message, please try again.');
            throw new Error((Array.isArray(data.errors) ? data.errors : []).join(', ') || 'Failed to update message');
          }

          if (data.data?.message) {
            window.dispatchEvent(
              new CustomEvent('output:internal:live-journal:batch-insert', {
                detail: { collection: 'messages', items: [data.data.message], channelId },
              }),
            );
          }

          onCancelEditing?.();
          // clear reply after completing edit
          setReplyTo(null);
        } catch (error) {
          logError('MessagesFooter', 'Failed to update message', error);
        }

        return;
      }

      const optimisticSortOrder = ulid();
      if (uploadIds.length > 0) {
        await setMediaAttachmentsOwner({
          uploadIds,
          ownerType: MediaAttachmentOwnerTypes.optimisticMessage,
          ownerId: optimisticMessageOwnerId(channelId, optimisticSortOrder),
          sessionKey: null,
        });
      }

      window.dispatchEvent(
        new CustomEvent('output:internal:live-journal:submit-message', {
          bubbles: true,
          detail: {
            messageData: {
              text: trimmedMessage,
              channel_id: channelId,
              workspace_id: workspaceId,
              optimistic_sort_order: optimisticSortOrder,
              // Pass upload rows + ids to live-journal; it decides whether to POST
              // now or wait for uploads to finish under an optimistic message.
              attachments: normalizedAttachments,
              upload_ids: uploadIds,
              reply_to_message_id: replyToMessageId,
            },
          },
        }),
      );
      onOwnMessageSubmitted?.();
      // clear reply after submitting
      setReplyTo(null);
    },
    [channelId, editingMessageId, messageBeingEdited, onCancelEditing, onOwnMessageSubmitted, replyToMessageId, workspaceId],
  );

  // TODO: remove these if they aren't used anywhere else
  const handleChange = useCallback(() => {}, []);
  const handleEmptyStateChange = useCallback(() => {}, []);

  const handleClearAttachments = useCallback(
    async (attachmentIds) => {
      const ids = attachmentIds.filter(Boolean);
      if (!ids.length || !workspaceId) return;

      const fromAttachmentId = await db.media_attachments.where('attachment_id').anyOf(ids).toArray();
      const fromRowId = await db.media_attachments.where('id').anyOf(ids).toArray();
      const byPk = new Map();
      [...fromAttachmentId, ...fromRowId].forEach((row) => {
        if (row?.workspace_id === workspaceId) byPk.set(row.id, row);
      });
      const toDelete = [...byPk.keys()];
      if (toDelete.length) await db.media_attachments.bulkDelete(toDelete);
    },
    [workspaceId],
  );

  const handleClearReply = useCallback(() => {
    setReplyTo(null);
  }, []);

  // bail if there is no profile (eg a shadow viewer)
  if (!currentProfileId) return null;

  return (
    <div className="pointer-events-auto relative pb-6">
      <div className="non-interactive-entities relative w-full">
        {isInteractive && (
          <RichdownComposer
            key={channelId}
            ref={composerRef}
            editorId={editorId}
            workspaceId={workspaceId}
            mainChannelId={mainChannelId}
            placeholder={!currentChannel ? 'Message' : `Message in ${getChannelDisplayName(currentChannel)}`}
            variant="channel"
            hideToolbar={false}
            onChange={handleChange}
            onSubmit={handleSubmitMessage}
            onEmptyStateChange={handleEmptyStateChange}
            editingMessageId={editingMessageId}
            onCancelEditing={onCancelEditing}
            editingMessage={messageBeingEdited}
            onClearAttachments={handleClearAttachments}
            dropzoneHelpers={dropzoneHelpers}
            onDropzoneStateChange={onDropzoneStateChange}
            onUploadCountChange={onUploadCountChange}
            maxFiles={maxMediaAttachments}
            maxRichdownContentSize={maxRichdownContentSize}
            replyToMessageId={replyToMessageId}
            replyToMessage={replyTo?.message ?? null}
            onClearReply={handleClearReply}
            channelId={channelId}
          />
        )}
      </div>
    </div>
  );
};

export default MessagesFooter;
